import {
  combine,
  withContext,
  withCallbacks,
  withStateHandlers,
  withEffect,
} from 'react-hooks-combine'
import { StoreContext } from 'store/context'
import { withRouter } from 'react-router'
import { ServicesContext } from 'services/context'
import fp from 'lodash/fp'
import { toaster } from 'utils/toast-wrapper'
import { ROLES } from 'constants/account.roles'
import axios from 'axios'
import { profile } from 'routes/route.map'

import DonationsDrawerComponent from './component'

const DONATION_RECEIPTS_START_YEAR = process.env.REACT_APP_DONATION_RECEIPTS_START_YEAR || 2024

const initialLoadingStates = () => {
  const currentYear = new Date().getFullYear()
  const states = {}

  for (let year = currentYear; year >= DONATION_RECEIPTS_START_YEAR; year--) {
    states[year] = { download: false, email: false }
  }

  return states
}

export const DonationsDrawer = combine({
  hocs: [withRouter],
  hooks: [
    withStateHandlers(
      {
        loadingStates: initialLoadingStates(),
        donationYears: [],
      },
      {
        setState: ({ args: [state] }) => state,
      }
    ),
    withContext('appStore', StoreContext),
    withContext(
      'sponsorsService',
      ServicesContext,
      ({ sponsorsService }) => sponsorsService
    ),
    withContext(
      'memberService',
      ServicesContext,
      ({ memberService }) => memberService
    ),
    withContext('loggedEntity', StoreContext, fp.get('auth.loggedEntity')),

    withEffect(
      ({ memberService, setState }) => {
        const fetchDonationYears = async () => {
          try {
            const donationAmounts = await memberService.getDonationAmounts()
            const years = Object.keys(donationAmounts)
              .filter(year => donationAmounts[year] > 0)
              .map(year => Number.parseInt(year, 10))
              .sort((a, b) => b - a) // Sort in descending order

            setState({ donationYears: years })
          } catch (error) {
            console.error('Failed to fetch donation years:', error)
          }
        }

        fetchDonationYears()
      },
      []
    ),

    withCallbacks(
      {
        onClickDownload:
          ({ memberService, sponsorsService, setState, loggedEntity, loadingStates }) =>
            async (year, isEmailRequest = false) => {
              try {
                setState({
                  loadingStates: {
                    ...loadingStates,
                    [year]: {
                      ...loadingStates[year],
                      [isEmailRequest ? 'email' : 'download']: true
                    }
                  }
                })

                if (loggedEntity.role === ROLES.SPONSOR) {
                  await sponsorsService.downloadDonations(year)
                } else {
                  if (isEmailRequest) {
                    await axios.post(`/member/report/donations/${year}`)
                    toaster.success('Email request successful')
                  } else {
                    await memberService.downloadDonations(year)
                    toaster.success('Download successful')
                  }
                }

                setState({
                  loadingStates: {
                    ...loadingStates,
                    [year]: {
                      ...loadingStates[year],
                      [isEmailRequest ? 'email' : 'download']: false
                    }
                  }
                })
              } catch (error) {
                setState({
                  loadingStates: {
                    ...loadingStates,
                    [year]: {
                      ...loadingStates[year],
                      [isEmailRequest ? 'email' : 'download']: false
                    }
                  }
                })
                toaster.error(isEmailRequest ? 'Email request failed' : 'Download failed')
              }
            },
      },
      ['loadingStates']
    ),
  ],
  transformProps: ({
    appStore,
    onClickDownload,
    loadingStates,
    loggedEntity,
    donationYears,
    history,
    ...rest
  }) => {
    return {
      ...rest,
      onClickDownload,
      loggedEntity,
      loadingStates,
      donationYears,
      appConfig: appStore.appConfig,
      history,
    }
  },
})(DonationsDrawerComponent)
